


















































import {
  defineComponent, PropType, ref, computed, onMounted, onUnmounted, watch,
} from '@vue/composition-api';
import { Radio } from '@hb/coffee';
import { openThirdPage } from '@/api/native/utils';
import { RightHelper } from '@/shared/right-router-helper';
import { RightPackageTypeEnum, SuperMemberSourceEnum, SuperCardSummaryBackType } from '@/api/rights/types';
import { ForceReadService, ForceReadServiceReturnType } from '@/widgets/right-contract';
import { RIGHTS_AGREEMENT, RIGHTS_AGREEMENT_CHECK, RIGHTS_ENTER } from '@/shared/right-utils/ubtRights';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { RightArrowOutline12 } from '@hb/coffee-icons';

// [功能说明]: 体验优化-提现流程优化页面
export default defineComponent({
  name: 'WithdrawOptimization',
  components: {
    Radio,
    RightArrowOutline12,
  },
  props: {
    superCardInfo: {
      type: Object as PropType<SuperCardSummaryBackType | null>,
      default: null,
    },
    productId: {
      type: String,
      default: '',
    },
    userTag: {
      type: String,
      default: '',
    },
  },
  emits: ['agreement-change'],
  setup(props, { emit }) {
    // 协议勾选状态 - 组件内部管理
    const agreementChecked = ref(false);

    // 协议服务实例
    let superMemberContractInstance: ForceReadServiceReturnType | null = null;

    // 与后端约定：该值表示「实验组」提现页样式；其余不存在或者不等于该值表示对照组
    const QUICK_WITHDRAW_CARD_STYLE_EXPERIMENT = 'styleB';

    const showSuperCard = computed(
      () => props.superCardInfo && Object.keys(props.superCardInfo).length > 0,
    );

    // 是否为实验组样式（提现页样式）
    const isExperimentStyle = computed(
      () => props.superCardInfo?.extraInfo?.style === QUICK_WITHDRAW_CARD_STYLE_EXPERIMENT,
    );

    // 标题：由前端先实现，后端文案无需修改
    const displayTitle = computed(
      () => (isExperimentStyle.value ? '加入会员 享最高480元返现红包' : '加入会员 立享场景定制权益'),
    );

    // 标题以第一个空格为界拆成两段，用于纯 CSS 双色样式（前段一色、后段一色）
    const splitIndex = computed(() => displayTitle.value.indexOf(' '));

    const displayTitleHead = computed(() => {
      const t = displayTitle.value;
      const idx = splitIndex.value;
      return idx >= 0 ? t.slice(0, idx + 1) : t;
    });
    const displayTitleTail = computed(() => {
      const t = displayTitle.value;
      const idx = splitIndex.value;
      return idx >= 0 ? t.slice(idx + 1) : '';
    });

    const disclaimerText = '权益由臻有钱及权益供应商提供，与最终放款机构无关';
    const agreementPrefix = '我已阅读并同意';
    const contractScene = SuperMemberSourceEnum.FINANCE_HOME_CREDIT_LOAN_APPLY;

    // 计算属性：埋点参数
    const tqParams = computed(() => {
      const {
        supplierCode, packageId, packageType, packageBuyStatus, sellPrice,
      } = props.superCardInfo || {};
      return {
        supplierCode,
        sellPrice,
        packageId,
        packageType,
        packageBuyStatus,
        productId: props.productId,
        from: SuperMemberSourceEnum.FINANCE_HOME_CREDIT_LOAN_APPLY,
        newSuperMemberUserCheckTag: props.userTag,
      };
    });

    // 计算属性：格式化价格显示
    const formattedPrice = computed(() => {
      const price = props.superCardInfo?.sellPrice || 79;
      const originalPrice = props.superCardInfo?.originalPrice;

      if (originalPrice && Number(originalPrice) > Number(price)) {
        return `自动续费${price}元/月`;
      }
      return `自动续费${price}元/月`;
    });

    // 计算属性：协议名称
    const contractName = computed(() => {
      const contractInfo = props.superCardInfo?.contractInfo;
      const name = contractInfo?.name || '会员服务协议';
      // 确保协议名称包含书名号
      return name.startsWith('《') && name.endsWith('》') ? name : `《${name}》`;
    });

    // 计算属性：免责声明
    const computedDisclaimerText = computed(() => disclaimerText);

    // 计算属性：协议前缀
    const computedAgreementPrefix = computed(() => agreementPrefix);

    // 打开协议弹窗
    const openContractPop = async () => {
      if (superMemberContractInstance && contractScene) {
        const contractInfo = props.superCardInfo?.contractInfo;
        const handleRes = await superMemberContractInstance.handleForceRead({
          contractScene: contractInfo?.scene || contractScene,
          preview: true,
          tqParams: tqParams.value,
        }) || false;
        // 只有勾选时再改变勾选状态
        if (handleRes) {
          agreementChecked.value = true;
        }
      }
    };

    // 处理协议勾选变化 - 完全按照提现页逻辑实现
    const handleAgreementChange = async () => {
      if (agreementChecked.value) {
        ubtSetupPageButton(RIGHTS_AGREEMENT_CHECK, tqParams.value);

        // 按照提现页逻辑：只有勾选时才处理强制阅读
        if (superMemberContractInstance) {
          try {
            const contractInfo = props.superCardInfo?.contractInfo;
            const handleRes = await superMemberContractInstance.handleForceRead({
              packageType: props.superCardInfo?.packageType
              || RightPackageTypeEnum.SUPER_MEMBER_CARD,
              contractScene: contractInfo?.scene || contractScene,
              tqParams: tqParams.value,
            });

            // 提现页逻辑：handleRes || !this.check
            agreementChecked.value = handleRes || !agreementChecked.value;
            emit('agreement-change', agreementChecked.value);
          } catch (error) {
            // 出错时取反当前状态
            agreementChecked.value = !agreementChecked.value;
            emit('agreement-change', agreementChecked.value);
          }
        } else {
          // 没有协议服务，弹出协议预览弹窗
          try {
            await openContractPop();
          } catch (error) {
            // 预览失败，取反当前状态
            agreementChecked.value = !agreementChecked.value;
            emit('agreement-change', agreementChecked.value);
          }
        }
      } else {
        // 取消勾选，直接处理
        agreementChecked.value = false;
        emit('agreement-change', false);
      }
    };

    // 初始化协议服务（packageType 动态，与接口返回一致）
    const initContractService = () => {
      if (contractScene) {
        const pkgType = props.superCardInfo?.packageType || RightPackageTypeEnum.SUPER_MEMBER_CARD;
        superMemberContractInstance = ForceReadService({
          packageType: pkgType,
          forceReadFromScene: contractScene,
        });
      }
    };

    // 处理协议点击
    const handleContractClick = () => {
      ubtSetupPageButton(RIGHTS_AGREEMENT, tqParams.value);
      openContractPop();
    };

    // 处理超级会员卡片点击（packageType 动态，与接口返回一致）
    const handleSuperCardClick = () => {
      const pkgType = props.superCardInfo?.packageType || RightPackageTypeEnum.SUPER_MEMBER_CARD;
      const url = RightHelper.dispatchRouterByBuyStatus(
        pkgType,
        SuperMemberSourceEnum.FINANCE_HOME_CREDIT_LOAN_APPLY_PAGE_DETAIL,
      );
      openThirdPage(url);
    };

    // 获取权益提交参数（参考提现页实现）
    const getRightSubmitParams = () => {
      if (agreementChecked.value && props.superCardInfo) {
        const { packageId, packageType } = props.superCardInfo;
        return {
          packageId,
          packageType,
          packageFrom: SuperMemberSourceEnum.FINANCE_HOME_CREDIT_LOAN_APPLY_PAGE_DETAIL,
        };
      }
      return undefined;
    };

    // 监听 superCardInfo 变化，重新初始化协议服务 - 按照提现页逻辑
    watch(() => props.superCardInfo, (newInfo) => {
      if (newInfo && Object.keys(newInfo).length > 0) {
        // 重新初始化协议服务
        initContractService();

        // 埋点上报：卡片曝光
        if (newInfo.packageId) {
          ubtSetupModuleExpose(RIGHTS_ENTER, tqParams.value);
        }
      }
    }, { immediate: true });

    // 销毁协议服务实例 - 暴露给外部调用
    const destroyInstance = () => {
      if (superMemberContractInstance) {
        superMemberContractInstance.destroyInstance();
      }
    };
    // 生命周期钩子
    onMounted(() => {
      initContractService();
      // 埋点上报：卡片曝光
      if (props.superCardInfo?.packageId) {
        ubtSetupModuleExpose(RIGHTS_ENTER, tqParams.value);
      }
    });

    onUnmounted(() => {
      destroyInstance();
    });

    return {
      showSuperCard,
      agreementChecked,
      formattedPrice,
      displayTitle,
      displayTitleHead,
      displayTitleTail,
      isExperimentStyle,
      contractName,
      disclaimerText: computedDisclaimerText,
      agreementPrefix: computedAgreementPrefix,
      handleAgreementChange,
      handleContractClick,
      handleSuperCardClick,
      getRightSubmitParams,
      destroyInstance,
    };
  },
});
